import { useContext, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Sun, Moon, Monitor } from 'lucide-react';
import { SidebarContext } from 'src/contexts/SidebarContext';
import HeaderButtons from './Buttons';
import HeaderUserbox from './Userbox';
import HeaderNotifications from './Buttons/Notifications';
import RegionSelector from './RegionSelector';
import logoImageDark from '../../../Images/Logo/symbian-logo.svg';
import logoImageLight from '../../../Images/Logo/SymbianLogo.png';
import { useThemeMode } from '../../../theme/ThemeProvider';
import { motion, AnimatePresence } from 'framer-motion';
import { useRole } from 'src/contexts/RoleContext/RoleContext';

type ThemeMode = 'light' | 'dark' | 'system';
const themeCycle: ThemeMode[] = ['light', 'dark', 'system'];
const themeConfig: Record<ThemeMode, { icon: typeof Sun; label: string }> = {
  light: { icon: Sun, label: 'Light mode — click for Dark' },
  dark: { icon: Moon, label: 'Dark mode — click for System' },
  system: { icon: Monitor, label: 'System mode — click for Light' },
};

interface HeaderProps {
  isStandalone?: boolean;
}

function Header({ isStandalone = false }: HeaderProps) {
  const { sidebarToggle, toggleSidebar, isCollapsed } = useContext(SidebarContext);
  const { mode, setMode, isDark } = useThemeMode();
  const { role } = useRole();

  const cycleTheme = useCallback(() => {
    const idx = themeCycle.indexOf(mode);
    setMode(themeCycle[(idx + 1) % themeCycle.length]);
  }, [mode, setMode]);

  const { icon: ThemeIcon, label: themeLabel } = themeConfig[mode];

  return (
    <header
      className={[
        'fixed top-0 right-0 z-[1200] flex items-center justify-between h-[64px]',
        'transition-[left] duration-[280ms] ease-[cubic-bezier(0.22,1,0.36,1)]',
        isStandalone ? 'left-0' : isCollapsed ? 'left-0 lg:left-[72px]' : 'left-0 lg:left-sidebar',
        isDark
          ? 'bg-dark-chrome-header/95 shadow-dark-header'
          : 'bg-white/[0.97] shadow-[0_1px_0_rgba(0,0,0,0.06),0_4px_20px_-4px_rgba(0,0,0,0.06)]',
      ].join(' ')}
      style={{
        padding: '0 clamp(14px, 2vw, 24px)',
        backdropFilter: 'blur(28px) saturate(1.7)',
        WebkitBackdropFilter: 'blur(28px) saturate(1.7)',
      }}
    >
      {isDark && (
        <div className="absolute bottom-0 left-0 right-0 h-px pointer-events-none bg-gradient-to-r from-transparent via-dark-border-brand/30 to-transparent" />
      )}

      <div className="flex min-w-0 items-center gap-2 sm:gap-3">
        {!isStandalone && (
          <button
            className={[
              'lg:hidden relative p-2 rounded-xl min-h-[40px] min-w-[40px]',
              'flex items-center justify-center transition-all duration-200',
              isDark
                ? 'text-slate-300 hover:text-white hover:bg-white/[0.07]'
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100/80',
            ].join(' ')}
            onClick={toggleSidebar}
            aria-label="Toggle menu"
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={sidebarToggle ? 'close' : 'open'}
                initial={{ rotate: -90, opacity: 0, scale: 0.8 }}
                animate={{ rotate: 0, opacity: 1, scale: 1 }}
                exit={{ rotate: 90, opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.15 }}
              >
                {sidebarToggle ? <X size={20} /> : <Menu size={20} />}
              </motion.div>
            </AnimatePresence>
          </button>
        )}

        <Link to="/" className={`${isStandalone ? 'flex' : 'lg:hidden flex'} min-w-0 items-center`}>
          <img
            src={isDark ? logoImageLight : logoImageDark}
            alt="Symbian Health"
            className="h-5 sm:h-6 min-w-0 max-w-full sm:max-w-none object-contain"
          />
        </Link>
      </div>
      <div className="flex flex-shrink-0 items-center gap-0.5 sm:gap-1">
        {!isStandalone && (
          <>
            <HeaderButtons />
            <div className={[
              'hidden sm:block w-px h-[18px] mx-1',
              isDark ? 'bg-white/[0.09]' : 'bg-slate-200',
            ].join(' ')} />
          </>
        )}
        <motion.button
          whileTap={{ scale: 0.88 }}
          onClick={cycleTheme}
          className={[
            // Below 360px the row cannot hold it next to the logo.
            'relative flex max-[359px]:hidden items-center justify-center w-9 h-9 rounded-xl flex-shrink-0',
            'transition-all duration-200',
            isDark
              ? 'text-slate-400 hover:text-amber-300 hover:bg-white/[0.08]'
              : 'text-slate-400 hover:text-brand-600 hover:bg-brand-50',
          ].join(' ')}
          aria-label={themeLabel}
          title={themeLabel}
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={mode}
              initial={{ rotate: -25, opacity: 0, scale: 0.7 }}
              animate={{ rotate: 0, opacity: 1, scale: 1 }}
              exit={{ rotate: 25, opacity: 0, scale: 0.7 }}
              transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
            >
              <ThemeIcon size={18} strokeWidth={1.75} />
            </motion.div>
          </AnimatePresence>
        </motion.button>
        {!isStandalone && (
          <>
            <HeaderNotifications />
          </>
        )}
        <div className={[
          'w-px h-[18px] mx-1',
          isDark ? 'bg-white/[0.09]' : 'bg-slate-200',
        ].join(' ')} />
        {role === 'patient' && <RegionSelector />}
        <HeaderUserbox isStandalone={isStandalone} />
      </div>
    </header>
  );
}

export default Header;
